import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const C = { navy: "#1B3A5C", teal: "#2A9D8F", white: "#FFFFFF", muted: "#6B85A3" };

async function startCheckout(priceId: string, userId: string, userEmail: string, skipTrial = false): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  const { data, error } = await supabase.functions.invoke("create-checkout", {
    body: { priceId, userId, userEmail, skipTrial },
    headers: { Authorization: `Bearer ${session?.access_token}` },
  });
  if (data?.alreadySubscribed) throw new Error("already_subscribed");
  if (error) throw new Error(error.message ?? "Erreur lors de la création du paiement");
  if (data?.url) window.location.href = data.url;
  else throw new Error("URL de paiement introuvable");
}

function TrialModal({
  countdown,
  onClose,
  onSubscribeAnnual,
  onSubscribeMonthly,
  loading,
  showCountdown = true,
}: {
  countdown: { d: number; h: number; m: number };
  onClose: () => void;
  onSubscribeAnnual: () => void;
  onSubscribeMonthly: () => void;
  loading: boolean;
  showCountdown?: boolean;
}) {
  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
        backgroundColor: "rgba(27,58,92,.55)", padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: C.white, borderRadius: 20, maxWidth: 460, width: "100%",
          padding: "40px 32px", textAlign: "center",
          boxShadow: "0 24px 64px rgba(27,58,92,.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: 36, marginBottom: 12 }}>⭐</div>

        <h2 style={{ fontSize: 22, fontWeight: 800, color: C.navy, marginBottom: 12 }}>
          Sécurisez votre accès Pro
        </h2>

        <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.7, marginBottom: 20 }}>
          Vous profitez actuellement de toutes les fonctionnalités Pro.
          Choisissez la formule qui vous convient pour continuer après votre essai.
        </p>

        {showCountdown && (
          <div
            style={{
              backgroundColor: "#F7FAFE", borderRadius: 12,
              padding: "12px 20px", marginBottom: 24, fontSize: 14, color: C.muted,
            }}
          >
            Temps restant sur votre essai :{" "}
            <strong style={{ color: C.navy }}>
              {countdown.d}j {countdown.h}h {countdown.m}min
            </strong>
          </div>
        )}

        {/* Annual — primary */}
        <div
          style={{
            backgroundColor: C.navy, borderRadius: 14, padding: "20px 20px 16px",
            marginBottom: 12, position: "relative",
          }}
        >
          <span
            style={{
              position: "absolute", top: -10, right: 16,
              backgroundColor: C.teal, color: C.white,
              fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
            }}
          >
            -94€/an
          </span>
          <p style={{ color: "rgba(255,255,255,.7)", fontSize: 13, marginBottom: 6 }}>
            Le choix de 80% de nos utilisateurs
          </p>
          <p style={{ color: C.white, fontSize: 18, fontWeight: 800, marginBottom: 4 }}>
            39€/mois{" "}
            <span style={{ fontSize: 13, fontWeight: 400, color: "rgba(255,255,255,.5)", textDecoration: "line-through" }}>
              47€
            </span>
          </p>
          <p style={{ color: "rgba(255,255,255,.5)", fontSize: 12, marginBottom: 14 }}>
            facturé 470€/an · soit 1,29€ par jour de cabinet
          </p>
          <button
            onClick={onSubscribeAnnual}
            disabled={loading}
            style={{
              width: "100%", padding: "13px 20px",
              backgroundColor: loading ? "#9CA3AF" : C.teal,
              color: C.white, borderRadius: 12, border: "none",
              fontWeight: 700, fontSize: 15,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Redirection…" : "Choisir l'annuel à 39€/mois →"}
          </button>
        </div>

        {/* Monthly — secondary */}
        <button
          onClick={onSubscribeMonthly}
          disabled={loading}
          style={{
            width: "100%", padding: "12px 20px",
            background: "none", border: "1px solid #D6E4F0", borderRadius: 12,
            color: C.muted, cursor: loading ? "not-allowed" : "pointer",
            fontSize: 13, fontWeight: 600, marginBottom: 8,
          }}
        >
          Préférer le mensuel à 47€/mois
        </button>

        <p style={{ fontSize: 11, color: C.muted, marginBottom: 16 }}>
          Sans engagement. Résiliable à tout moment.
        </p>

        <button
          onClick={onClose}
          style={{
            background: "none", border: "none",
            color: C.muted, cursor: "pointer", fontSize: 12, opacity: 0.7,
          }}
        >
          Fermer
        </button>
      </div>
    </div>,
    document.body,
  );
}

export default function FoundingBanner() {
  const { user } = useAuth();
  const { isTrial, trialUsed, isActive, daysLeftInTrial, hoursLeftInTrial, minutesLeftInTrial } = useSubscription();
  const isPostTrial = trialUsed && !isTrial && !isActive;
  const [showModal, setShowModal] = useState(false);
  const [compact, setCompact] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [countdown, setCountdown] = useState({
    d: daysLeftInTrial, h: hoursLeftInTrial, m: minutesLeftInTrial,
  });

  useEffect(() => {
    setCountdown({ d: daysLeftInTrial, h: hoursLeftInTrial, m: minutesLeftInTrial });
  }, [daysLeftInTrial, hoursLeftInTrial, minutesLeftInTrial]);

  useEffect(() => {
    if (!isTrial) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        let { d, h, m } = prev;
        m -= 1;
        if (m < 0) { m = 59; h -= 1; }
        if (h < 0) { h = 23; d -= 1; }
        if (d < 0) return { d: 0, h: 0, m: 0 };
        return { d, h, m };
      });
    }, 60_000);
    return () => clearInterval(timer);
  }, [isTrial]);

  const handleSubscribe = useCallback(async (annual = false) => {
    if (!user) return;
    const priceId = annual
      ? import.meta.env.VITE_STRIPE_PRICE_PRO_ANNUAL
      : import.meta.env.VITE_STRIPE_PRICE_PRO;
    if (!priceId) {
      toast.error("Configuration paiement manquante");
      return;
    }
    setCheckoutLoading(true);
    try {
      await startCheckout(priceId, user.id, user.email ?? "", annual);
    } catch (e: any) {
      if (e?.message === "already_subscribed") {
        toast.warning("Vous avez déjà un abonnement Pro actif");
        window.location.href = "/dashboard";
        return;
      }
      toast.error("Erreur — réessayez ou contactez contact@freedomarchitect.io");
      setCheckoutLoading(false);
    }
  }, [user]);

  const handleDismiss = () => { setShowModal(false); setCompact(true); };

  if (!isTrial && !isPostTrial) return null;

  const bannerHeight = 44;

  return (
    <>
      <div
        style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
          height: bannerHeight, backgroundColor: C.navy,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", transition: "height .2s", padding: "0 16px",
        }}
        onClick={() => setShowModal(true)}
      >
        {isTrial ? (
          /* ── Bannière trial actif ── */
          <span style={{ color: C.white, fontSize: 13, fontWeight: 600, textAlign: "center" }}>
            Essai Pro ·{" "}
            <strong>{countdown.d}j {countdown.h}h {countdown.m}min restants</strong>{" "}
            · à partir de 39€/mois →
          </span>
        ) : (
          /* ── Bannière post-trial ── */
          <span style={{ color: C.white, fontSize: 13, fontWeight: 600, textAlign: "center" }}>
            Essai terminé · Plan gratuit actif (10 recettes/mois) ·{" "}
            <strong style={{ color: "#2DD4BF" }}>Passer au Pro →</strong>
          </span>
        )}
      </div>
      <div style={{ height: bannerHeight }} />
      {showModal && (
        <TrialModal
          countdown={countdown}
          onClose={handleDismiss}
          onSubscribeAnnual={() => handleSubscribe(true)}
          onSubscribeMonthly={() => handleSubscribe(false)}
          loading={checkoutLoading}
          showCountdown={isTrial}
        />
      )}
    </>
  );
}
